import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  Menu,
  X,
  Upload,
  Download,
  UserCircle,
  LogOut,
  FileText,
  ClipboardList,
} from "lucide-react";
import { ExportPopup } from "./ExportPopup";
import { ImportPopup } from "./ImportPopup";


const Dropdown = ({
  title,
  links,
  isMobile = false,
  onClose,
  isAdmin = false,
  userRole = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSubMenu, setActiveSubMenu] = useState(null);
  const dropdownRef = useRef(null);

  // Get the appropriate indent route based on user role
  const getIndentRoute = (role) => {
    if (role === "location head") return "/indent2";
    if (["director", "store head", "purchase head", "store officer", "purchase department"].includes(role)) {
      return "/indent3";
    }
    if (role === "admin") return "/indent";
    return "/indent"; // default fallback
  };

  // Modify links if they are indent-related
  const processedLinks = links.map(link => {
    if (link.name === "Indents" || link.name === "All Indent view") {
      return {
        ...link,
        href: getIndentRoute(userRole)
      };
    }
    return link;
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setActiveSubMenu(null);
        onClose && onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  const toggleOpen = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setActiveSubMenu(null);
    }
  };

  const handleLinkClick = () => {
    setIsOpen(false);
    setActiveSubMenu(null);
    onClose && onClose();
  };

  const renderIndentSubMenu = () => {
    const indentRoutes = {
      PE: "/peindent",
      L1: "/l1page",
      L2: "/L2Page",
      PD: "/POPage",
      PH: "/PHPage",
      SO: "/IRPage",
      SH: "/SHPage",
    };

    if (isMobile) {
      return (
        <div className="bg-gray-50 py-1">
          {Object.entries(indentRoutes).map(([label, route]) => (
            <Link
              key={label}
              to={route}
              className="block px-8 py-2 text-sm text-gray-700 hover:bg-gray-100"
              onClick={handleLinkClick}
            >
              {label}
            </Link>
          ))}
        </div>
      );
    }

    return (
      <div className="absolute left-full top-0 w-32 bg-white shadow-lg rounded-lg border border-gray-100 py-1">
        {Object.entries(indentRoutes).map(([label, route]) => (
          <Link
            key={label}
            to={route}
            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            onClick={handleLinkClick}
          >
            {label}
          </Link>
        ))}
      </div>
    );
  };

  const renderLink = (link) => {
    if (link.name === "Indents" && isAdmin) {
      return (
        <div className={isMobile ? "" : "relative group"}>
          <button
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex justify-between items-center"
            onClick={() => {
              if (isMobile) {
                setActiveSubMenu(activeSubMenu === 'indents' ? null : 'indents');
              }
            }}
          >
            {link.name}
            <ChevronDown 
              className={`w-4 h-4 transform transition-transform duration-200 ${
                activeSubMenu === 'indents' ? 'rotate-180' : ''
              }`} 
            />
          </button>
          {((!isMobile && isAdmin) || (isMobile && activeSubMenu === 'indents')) && renderIndentSubMenu()}
        </div>
      );
    }

    return (
      <Link
        to={link.href}
        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
        onClick={handleLinkClick}
      >
        {link.name}
      </Link>
    );
  };

  if (isMobile) {
    return (
      <div ref={dropdownRef} className="border-b border-gray-200">
        <button
          onClick={toggleOpen}
          className="w-full flex justify-between items-center px-4 py-3 text-left text-gray-700 hover:bg-gray-100"
        >
          {title}
          <ChevronDown
            className={`w-5 h-5 transform transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>
        {isOpen && (
          <div className="bg-white">
            {processedLinks.map((link, index) => (
              <React.Fragment key={index}>
                {renderLink(link)}
              </React.Fragment>
            ))}
          </div>
        )}store_head
      </div>
    );
  }

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={toggleOpen}
        className="flex items-center text-white hover:text-gray-200 transition-colors duration-200 font-medium px-3 py-2 rounded-md focus:outline-none"
      >
        {title}
        <ChevronDown className="ml-1 w-4 h-4" />
      </button>
      {isOpen && (
        <div className="absolute z-20 mt-2 w-56 bg-white shadow-lg rounded-lg border border-gray-100 py-1">
          {processedLinks.map((link, index) => (
            <React.Fragment key={index}>
              {renderLink(link)}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

const Header = ({ exportEndpoint, importEndpoint, currentIndentData }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [showExportPopup, setShowExportPopup] = useState(false);
  const [showImportPopup, setShowImportPopup] = useState(false);
  const [activeDesktopDropdown, setActiveDesktopDropdown] = useState(null);
  const navigate = useNavigate();
  const { role } = useSelector((state) => state.auth);
  const username = useSelector((state) => state.auth.username);
  const isAdmin = role === "admin";

  const getIndentRoute = () => {
    const userRole = role?.toLowerCase();
    if (userRole === "location head") return "/indent2";
    if (["director", "store head", "purchase head", "store officer", "purchase department"].includes(userRole)) {
      return "/indent3";
    }
    if (["admin", "project engineer"].includes(userRole)) return "/indent";
    return "/indent";
  };

  const getRoleButtons = () => {
    const currentRole = role?.toLowerCase() || "";

    const roleConfig = {
      "project engineer": [{ text: "PE", href: "/peindent" }],
      "location head": [{ text: "L1", href: "/l1page" }],
      "director": [{ text: "L2", href: "/L2Page" }],
      "purchase department": [{ text: "PD", href: "/POPage" }],
      "purchase head": [{ text: "PH", href: "/PHPage" }],
      "store officer": [{ text: "SO", href: "/IRPage" }],
      "store head": [
        { text: "SH", href: "/SHPage" },
        { text: "SO", href: "/IRPage" },
      ],
    };

    return roleConfig[currentRole] || [];
  };

  const handleLogout = () => {
    navigate("/login");
  };

  const closeAllDropdowns = () => {
    setActiveDesktopDropdown(null);
  };

  const roleButtons = getRoleButtons();

  const renderRoleButtons = () => {
    if (!Array.isArray(roleButtons) || roleButtons.length === 0) {
      return null;
    }

    return roleButtons.map((button, index) => (
      <Link
        key={index}
        to={button.href}
        className="bg-white text-[#1e3a8a] px-4 py-2 rounded-md flex items-center hover:bg-gray-100 transition-colors duration-200"
      >
        <ClipboardList className="mr-2 w-4 h-4" />
        {button.text}
      </Link>
    ));
  };

  const indentViewRoute = getIndentRoute();

  return (
    <>
      {/* Mobile Header */}
      <header className="bg-[#1e3a8a] shadow-md md:hidden">
        <div className="px-4 flex items-center justify-between h-16">
          <Link
            to="/dashboard"
            className="text-lg font-bold text-white hover:text-gray-200"
          >
            Indent Mgmt
          </Link>

          <div className="flex items-center space-x-3">
            <Link
              to={indentViewRoute}
              className="bg-white text-[#1e3a8a] px-3 py-1.5 rounded-md text-sm flex items-center hover:bg-gray-100"
            >
              <FileText className="mr-1 w-4 h-4" />
              Indents
            </Link>
            <button
              onClick={() => setMobileNavOpen(true)}
              className="text-white p-2"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      {/* Desktop Header */}
      <header className="bg-[#1e3a8a] shadow-md hidden md:block">
        <div className="container mx-auto px-4 flex items-center justify-between h-16">
          <Link
            to="/dashboard"
            className="text-xl font-bold text-white hover:text-gray-200"
            onClick={closeAllDropdowns}
          >
            Indent Management
          </Link>

          <nav className="flex items-center space-x-4">
            <Dropdown
              title="Home"
              links={[
                { name: "Dashboard", href: "/dashboard" },
                { name: "Documentation", href: "/documentation" },
                { name: "Developed By", href: "/developed-by" },
                { name: "Contact Us", href: "/contact" },
              ]}
              onClose={closeAllDropdowns}
              userRole={role?.toLowerCase()}
            />
            <Dropdown
              title="Entry"
              links={[
                { name: "Indents", href: indentViewRoute },
                { name: "All Indent view", href: indentViewRoute },
                { name: "MIN", href: "/min" },
                { name: "PO", href: "/po" },
                { name: "Force Close", href: "/force-close" },
                { name: "History", href: "/history" },
              ]}
              isAdmin={isAdmin}
              onClose={closeAllDropdowns}
              userRole={role?.toLowerCase()}
            />
            <Dropdown
              title="Master"
              links={[
                { name: "Location", href: "/mlocation" },
                { name: "Project", href: "/mproject" },
                { name: "Category", href: "/mcategory" },
                { name: "Unit", href: "/mau" },
                { name: "Items", href: "/mitem" },
              ]}
              onClose={closeAllDropdowns}
              userRole={role?.toLowerCase()}
            />
            <Dropdown
              title="Report"
              links={[
                { name: "Indent Open", href: "/indent-open" },
                { name: "PO Open", href: "/po-open" },
              ]}
              onClose={closeAllDropdowns}
              userRole={role?.toLowerCase()}
            />
            {isAdmin && (
              <Link
                to="/admin"
                className="text-white hover:text-gray-200 font-medium px-3 py-2 rounded-md"
                onClick={closeAllDropdowns}
              >
                Admin
              </Link>
            )}
          </nav>

          <div className="flex items-center space-x-4">
            <Link
              to={indentViewRoute}
              className="bg-white text-[#1e3a8a] px-4 py-2 rounded-md flex items-center hover:bg-gray-100 transition-colors duration-200"
            >
              <FileText className="mr-2 w-4 h-4" />
              All Indent View
            </Link>

            {renderRoleButtons()}

            <button
              className="bg-white text-[#1e3a8a] px-4 py-2 rounded-md flex items-center hover:bg-gray-100 transition-colors duration-200"
              onClick={() => setShowImportPopup(true)}
            >
              <Upload className="mr-2 w-4 h-4" />
              Import
            </button>
            <button
              className="bg-white text-[#1e3a8a] px-4 py-2 rounded-md flex items-center hover:bg-gray-100 transition-colors duration-200"
              onClick={() => setShowExportPopup(true)}
            >
              <Download className="mr-2 w-4 h-4" />
              Export
            </button>
            <button
              onClick={() => {
                setDrawerOpen(!drawerOpen);
                closeAllDropdowns();
              }}
              className="focus:outline-none"
            >
              <UserCircle className="w-8 h-8 text-white hover:text-gray-200" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Sidebar */}
      {mobileNavOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 md:hidden">
          <div className="bg-white w-64 h-full shadow-lg animate-slide-in-left overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b">
              <div>
                <h2 className="text-xl font-bold">Menu</h2>
                <p className="text-sm text-gray-600">{username}</p>
              </div>
              <button
                className="text-gray-600"
                onClick={() => setMobileNavOpen(false)}
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-4 border-b flex flex-wrap gap-2">
              {renderRoleButtons()}
            </div>

            <div className="divide-y divide-gray-200">
              <Dropdown
                title="Home"
                isMobile
                links={[
                  { name: "Dashboard", href: "/dashboard" },
                  { name: "Documentation", href: "/documentation" },
                  { name: "Developed By", href: "/developed-by" },
                  { name: "Contact Us", href: "/contact" },
                ]}
              />
              <Dropdown
                title="Entry"
                isMobile
                links={[
                  { name: "Indents", href: indentViewRoute },
                  { name: "MIN", href: "/min" },
                  { name: "PO", href: "/po" },
                  { name: "Force Close", href: "/force-close" },
                  { name: "History", href: "/history" },
                ]}
                isAdmin={isAdmin}
                userRole={role?.toLowerCase()}
              />
              <Dropdown
                title="Master"
                isMobile
                links={[
                  { name: "Location", href: "/mlocation" },
                  { name: "Project", href: "/mproject" },
                  { name: "Category", href: "/mcategory" },
                  { name: "Unit", href: "/mau" },
                  { name: "Items", href: "/mitem" },
                ]}
                userRole={role?.toLowerCase()}
              />
              <Dropdown
                title="Report"
                isMobile
                links={[
                  { name: "Indent Open", href: "/indent-open" },
                  { name: "PO Open", href: "/po-open" },
                ]}
                userRole={role?.toLowerCase()}
              />
              {isAdmin && (
                <Link
                  to="/admin"
                  className="block px-4 py-3 text-gray-700 hover:bg-gray-100"
                >
                  Admin
                </Link>
              )}
            </div>

            <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-white">
              <div className="flex space-x-2">
                <button
                  className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-md flex items-center justify-center hover:bg-gray-200"
                  onClick={() => setShowImportPopup(true)}
                >
                  <Upload className="mr-2 w-4 h-4" />
                  Import
                </button>
                <button
                  className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-md flex items-center justify-center hover:bg-gray-200"
                  onClick={() => setShowExportPopup(true)}
                >
                  <Download className="mr-2 w-4 h-4" />
                  Export
                </button>
              </div>
              <button
                onClick={handleLogout}
                className="w-full mt-2 flex items-center justify-center space-x-2 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Drawer */}
      {drawerOpen && (
        <div className="fixed inset-y-0 right-0 max-w-xs w-full bg-white shadow-xl z-50 p-6">
          <button
            className="absolute top-4 right-4 text-gray-600"
            onClick={() => setDrawerOpen(false)}
          >
            <X className="w-6 h-6" />
          </button>
          <div className="mt-12">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <UserCircle className="w-10 h-10 text-gray-600" />
                <div>
                  <p className="font-medium">
                    {username || "No username found"}
                  </p>
                  <p className="text-sm text-gray-600">{role}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors duration-200"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Popup */}
      {showImportPopup && (
        <ImportPopup
          onClose={() => setShowImportPopup(false)}
          importEndpoint={importEndpoint}
        />
      )}

      {/* Export Popup */}
      {showExportPopup && (
        <ExportPopup
          onClose={() => setShowExportPopup(false)}
          exportEndpoint={exportEndpoint}
          indentData={currentIndentData}
          customFilename="indent-management-export"
        />
      )}
    </>
  );
};

export default Header;